import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatBalance } from '@/lib/format-number'

const EXIT_MS = 200

interface ResultModalProps {
	visible: boolean
	multiplier: number
	profit: number
	currencyCode: string
	onDismiss?: () => void
}

export function ResultModal({ visible, multiplier, profit, currencyCode, onDismiss }: ResultModalProps) {
	const [mounted, setMounted] = useState(visible)
	const [exiting, setExiting] = useState(false)
	const [snapshot, setSnapshot] = useState({ multiplier, profit, currencyCode })

	useEffect(() => {
		if (visible) {
			setSnapshot({ multiplier, profit, currencyCode })
			setMounted(true)
			setExiting(false)
			return
		}

		if (!mounted) {
			return
		}

		setExiting(true)
		const timer = window.setTimeout(() => {
			setMounted(false)
			setExiting(false)
		}, EXIT_MS)

		return () => window.clearTimeout(timer)
	}, [visible, mounted, multiplier, profit, currencyCode])

	if (!mounted) {
		return null
	}

	return (
		<button
			type='button'
			onClick={exiting ? undefined : onDismiss}
			className={cn(
				'absolute inset-0 z-20 flex items-center justify-center bg-background/40 p-4',
				exiting ? 'duration-200 animate-out fade-out fill-mode-forwards' : 'duration-200 animate-in fade-in',
			)}
		>
			<div
				className={cn(
					'w-full max-w-xs rounded-2xl border border-primary bg-card p-6 text-center shadow-lg animate-duration-fast',
					exiting ? 'animate-zoom-out' : 'animate-zoom-in',
				)}
			>
				<p className='text-2xl font-bold text-foreground'>You Won!</p>
				<p className='mt-3 text-3xl font-bold text-primary'>{snapshot.multiplier.toFixed(2)}×</p>
				<p className='mt-2 text-sm text-muted-foreground'>
					Profit <span className='font-semibold text-foreground'>{formatBalance(snapshot.profit, 4)}</span> {snapshot.currencyCode}
				</p>
			</div>
		</button>
	)
}
